import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ListTodo,
  Plus,
  XCircle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { patientQueries } from '@/features/patient-record/queries';
import type {
  PatientTaskStatus,
  PatientTaskSummaryViewModel,
} from '@/features/patient-record/types';

import { Panel } from '../Panel';
import { TaskCompleteButton } from './TaskCompleteButton';
import { TaskCreateDialog } from './TaskCreateDialog';
import { TaskEditDialog } from './TaskEditDialog';
import { TaskPriorityBadge } from './TaskPriorityBadge';
import { classifyDueDate, compareByDueThenPriority } from './taskHelpers';

interface TasksPanelProps {
  readonly guid: string;
  readonly dragHandle?: ReactNode;
  /** Render body-only (no Panel chrome) for embedding inside another card. See {@link Panel}. */
  readonly embedded?: boolean;
  /** Suppress the panel's own add-task button — the host (e.g. the pedigree node rail's
   *  CollapsibleCard header) provides it instead. */
  readonly hideHeaderAction?: boolean;
}

interface TaskRowProps {
  readonly guid: string;
  readonly task: PatientTaskSummaryViewModel;
  readonly onSelect: () => void;
}

/**
 * Per-patient tasks card (PTV2-1097).
 *
 * Active queue (status=TODO) renders at the top and is the panel's default
 * view. Closed tasks (status=DONE / CANCELLED) live behind a "Show closed"
 * toggle, collapsed by default — the PRD's "completion records who + when,
 * visible on the task entry" wording is satisfied by surfacing those rows
 * here with their completion metadata inline.
 */
export function TasksPanel({
  guid,
  dragHandle,
  embedded = false,
  hideHeaderAction = false,
}: TasksPanelProps): ReactElement {
  const { t } = useTranslation();
  const openQuery = useQuery(patientQueries.tasks(guid, { status: 'TODO' }));
  const closedQuery = useQuery({
    ...patientQueries.tasks(guid, { status: 'ALL' }),
    select: (all) =>
      all.filter((task) => task.status === 'DONE' || task.status === 'CANCELLED'),
  });
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);

  const openTasks = useMemo<readonly PatientTaskSummaryViewModel[]>(
    () => (openQuery.isSuccess ? sortByDueThenPriority(openQuery.data) : []),
    [openQuery.isSuccess, openQuery.data],
  );

  const closedTasks = useMemo<readonly PatientTaskSummaryViewModel[]>(
    () => (closedQuery.isSuccess ? sortByDueThenPriority(closedQuery.data) : []),
    [closedQuery.isSuccess, closedQuery.data],
  );

  const addButton = (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => {
        setCreating(true);
      }}
      aria-label={t('patient.tasks.addTaskAriaLabel')}
      className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
    >
      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
    </Button>
  );

  return (
    <>
      <Panel
        icon={<ListTodo className="h-3.5 w-3.5" aria-hidden="true" />}
        title={t('patient.sidePanels.tasks')}
        headerAction={hideHeaderAction ? undefined : addButton}
        collapsible
        dragHandle={dragHandle}
        embedded={embedded}
      >
        {openQuery.isPending && (
          <p className="px-4 py-3 text-[12.5px] text-muted-foreground italic">
            {t('patient.tasks.loading')}
          </p>
        )}
        {openQuery.isError && (
          <p role="alert" className="px-4 py-3 text-[12.5px] text-destructive">
            {t('patient.tasks.loadError')}
          </p>
        )}
        {openQuery.isSuccess && openTasks.length === 0 && (
          <p className="px-4 py-3 text-[12.5px] text-muted-foreground italic">
            {t('patient.tasks.empty')}
          </p>
        )}
        {openQuery.isSuccess && openTasks.length > 0 && (
          <ul className="divide-y divide-border">
            {openTasks.map((task) => (
              <li key={task.id}>
                <TaskRow
                  guid={guid}
                  task={task}
                  onSelect={() => {
                    setEditingId(task.id);
                  }}
                />
              </li>
            ))}
          </ul>
        )}

        {closedTasks.length > 0 && (
          <div className="border-t border-border bg-muted/10">
            <button
              type="button"
              onClick={() => {
                setShowClosed((v) => !v);
              }}
              aria-expanded={showClosed}
              className="flex w-full items-center gap-1.5 px-4 py-2 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            >
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  'h-3 w-3 transition-transform',
                  !showClosed && '-rotate-90',
                )}
              />
              {t('patient.tasks.showClosed', { count: closedTasks.length })}
            </button>
            {showClosed && (
              <ul className="divide-y divide-border">
                {closedTasks.map((task) => (
                  <li key={task.id}>
                    <TaskRow
                      guid={guid}
                      task={task}
                      onSelect={() => {
                        setEditingId(task.id);
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Panel>
      {creating && (
        <TaskCreateDialog
          guid={guid}
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
      {editingId !== null && (
        <TaskEditDialog
          guid={guid}
          taskId={editingId}
          onClose={() => {
            setEditingId(null);
          }}
        />
      )}
    </>
  );
}

function TaskRow({ guid, task, onSelect }: TaskRowProps): ReactElement {
  const { t } = useTranslation();
  const due = task.dueDate;
  const dueStatus = classifyDueDate(due);
  const isClosed = task.status === 'DONE' || task.status === 'CANCELLED';

  return (
    <div
      className={cn(
        'group/task flex w-full items-start gap-2.5 px-4 py-2.5 hover:bg-muted/40 transition-colors focus-within:bg-muted/60',
        isClosed && 'opacity-70',
      )}
    >
      {isClosed ? (
        <ClosedIndicator status={task.status} />
      ) : (
        <TaskCompleteButton guid={guid} taskId={task.id} className="mt-0.5" />
      )}
      <button
        type="button"
        onClick={onSelect}
        className="min-w-0 flex-1 text-left focus-visible:outline-hidden"
        aria-label={t('patient.tasks.openTaskAriaLabel', { title: task.title })}
      >
        <p
          className={cn(
            'text-[13px] font-medium leading-tight break-words',
            task.status === 'CANCELLED'
              ? 'text-muted-foreground line-through'
              : 'text-foreground',
          )}
        >
          {task.title}
        </p>
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          <TaskPriorityBadge priority={task.priority} />
          {!isClosed && due !== null && (
            <span
              className={cn(
                'text-[10.5px] inline-flex items-center gap-0.5 tabular-nums',
                dueStatus === 'overdue'
                  ? 'text-destructive font-semibold'
                  : dueStatus === 'soon'
                    ? 'text-warning font-semibold'
                    : 'text-muted-foreground',
              )}
            >
              {dueStatus === 'overdue' && (
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              )}
              {formatDate(due)}
            </span>
          )}
          {isClosed && (
            <span className="text-[10.5px] text-muted-foreground">
              {t(`patient.tasks.status.${task.status}`)}
            </span>
          )}
        </div>
      </button>
    </div>
  );
}

function ClosedIndicator({
  status,
}: {
  readonly status: PatientTaskStatus;
}): ReactElement | null {
  if (status === 'DONE') {
    return <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0 mt-0.5" aria-hidden="true" />;
  }
  if (status === 'CANCELLED') {
    return (
      <XCircle className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
    );
  }
  return null;
}

function sortByDueThenPriority(
  tasks: readonly PatientTaskSummaryViewModel[],
): readonly PatientTaskSummaryViewModel[] {
  return [...tasks].sort(compareByDueThenPriority);
}
